import { Box, Text } from '@tocktix/pantry';
import { DisplayTime } from '@tock/time';
import { IconPeopleFamily } from '@tock/icons';
import { PartyForCard } from 'components/service/party/partyRedesignCard/partyType';
import {
  isTimeRange,
  usePartyHeader,
} from 'components/service/party/partyRedesignCard/usePartyHeader';
import { partyCardHeaderCssDefs } from 'components/service/party/partyRedesignCard/partyCardCssDefs';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import GdaBadge from 'components/global-dining-access/GdaBadge';
import clsx from 'clsx';

interface Props {
  party: PartyForCard;
}

const PartyRedesignHeader: React.FC<Props> = ({ party }) => {
  const isDashboardGdaEnabled = useFeatureFlag('TOCK_DASHBOARD_GDA');
  const { name, isLargeParty, expectedPartySize, time, hasGlobalDiningAccess } =
    usePartyHeader(party);

  const renderName = (
    <Box display="flex" alignItems="center" gap={50}>
      <Text variant="h4" data-testid="party-name" margin={[0, 25, 0, 0]}>
        {name}
      </Text>
      {isDashboardGdaEnabled && hasGlobalDiningAccess && <GdaBadge variant="badge" />}
    </Box>
  );

  let firstLineContent = null;
  let trailingContent = null;

  if (time) {
    firstLineContent = isTimeRange(time) ? (
      <Text variant="caption" data-testid="waitlist-range">
        <DisplayTime timeValue={time.startDateTime} />
        {` - `}
        <DisplayTime timeValue={time.endDateTime} />
      </Text>
    ) : (
      <Text variant="caption" data-testid="booking-time">
        <DisplayTime timeValue={time} />
      </Text>
    );
    trailingContent = renderName;
  } else {
    firstLineContent = renderName;
  }

  return (
    <>
      <div css={partyCardHeaderCssDefs.header({ hasTime: time !== undefined })}>
        {firstLineContent}
        <div
          // This affects the styling of the div -- in a manner that is completely within
          // emotion styles -- by using nested styles underneath &:hover variants of parent
          // component styles. emotion doesn't offer a great built-in solution for this
          // but we can apply styles to a custom data- attribute easily enough.
          data-party-size-indicator={!isLargeParty}
          data-testid="party-size"
          className={clsx(!isLargeParty && 'partySizeIndicator')}
          css={partyCardHeaderCssDefs.partySizeIndicator({ isLargeParty, isCompactView: false })}
        >
          {isLargeParty && <IconPeopleFamily css={partyCardHeaderCssDefs.icon} />}
          <Text variant="bodyStrong" color={isLargeParty ? 'fgWarningStrong' : undefined}>
            {expectedPartySize}
          </Text>
        </div>
      </div>
      {trailingContent}
    </>
  );
};

export default PartyRedesignHeader;
